package com.fantasytenis.web.screens.contests

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.contests.entity.ContestEntity
import com.fantasytenis.shared.domain.contests.entity.ContestStatus
import com.fantasytenis.shared.domain.contests.entity.EntryEntity
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.lineup.entity.CategoryBreakdown
import com.fantasytenis.shared.domain.lineup.entity.LineupEntity
import com.fantasytenis.shared.domain.tennis.entity.TennisPlayerEntity
import com.fantasytenis.web.components.AvatarDisplay
import com.fantasytenis.web.components.AvatarSize
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.ContestStatusBadge
import com.fantasytenis.web.components.ContestTypeBadge
import com.fantasytenis.web.components.PerfectLineupSection
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.hooks.useContestSubscription
import com.fantasytenis.web.hooks.useRealtime
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.Theme
import com.fantasytenis.web.theme.ThemeColors
import com.fantasytenis.web.theme.borderTop
import com.fantasytenis.web.theme.borderBottom
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Screen showing a contest entry's lineup and details.
 * Can view own entries or other users' entries from the leaderboard.
 * Supports real-time score updates for LIVE contests.
 */
@Composable
fun ContestEntryViewScreen(contestId: String, entryId: String) {
    val scope = rememberCoroutineScope()
    val colors = Theme.colors
    val currentUserId = SharedData.currentUser.collectAsState().value?.id

    var contest by remember { mutableStateOf<ContestEntity?>(null) }
    var entry by remember { mutableStateOf<EntryEntity?>(null) }
    var lineup by remember { mutableStateOf<LineupEntity?>(null) }
    var players by remember { mutableStateOf<List<TennisPlayerEntity>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var lastUpdateTime by remember { mutableStateOf<Long?>(null) }

    val isOwnEntry = entry?.userId == currentUserId

    // Initialize realtime connection
    val realtimeState = useRealtime()

    // Function to reload entry and lineup data
    suspend fun reloadData() {
        // Reload entry from leaderboard
        DependencyContainer.getContestLeaderboardUseCase(contestId, limit = 1000).fold(
            onSuccess = { entries ->
                val updatedEntry = entries.find { it.id == entryId }
                if (updatedEntry != null) {
                    entry = updatedEntry
                }
            },
            onFailure = { /* Ignore errors on refresh */ }
        )

        // Reload lineup to get updated scores
        entry?.lineupId?.let { lineupId ->
            DependencyContainer.getLineupUseCase(lineupId).fold(
                onSuccess = { lineup = it },
                onFailure = { /* Ignore errors on refresh */ }
            )
        }
    }

    // Subscribe to contest updates for live scoring
    useContestSubscription(
        contestId = contestId,
        onLeaderboardUpdate = { update ->
            // When leaderboard updates, refresh our entry data
            lastUpdateTime = update.timestamp
            scope.launch {
                reloadData()
            }
        },
        onEntryUpdate = { update ->
            // If this is our entry being updated, refresh immediately
            if (update.entryId == entryId || update.userId == entry?.userId) {
                scope.launch {
                    reloadData()
                }
            }
        }
    )

    // Initial data load
    LaunchedEffect(contestId, entryId) {
        isLoading = true
        error = null

        // Load contest
        DependencyContainer.getContestUseCase(contestId).fold(
            onSuccess = { contest = it },
            onFailure = { error = it.message }
        )

        // Load leaderboard to find the entry
        DependencyContainer.getContestLeaderboardUseCase(contestId, limit = 1000).fold(
            onSuccess = { entries ->
                entry = entries.find { it.id == entryId }
                if (entry == null) {
                    error = "Entry not found"
                }
            },
            onFailure = { error = it.message }
        )

        // If we have the entry and it has a lineup, load it
        entry?.lineupId?.let { lineupId ->
            DependencyContainer.getLineupUseCase(lineupId).fold(
                onSuccess = { lineup = it },
                onFailure = { /* Lineup may not exist yet */ }
            )
        }

        // Load slate players for display
        contest?.let { c ->
            DependencyContainer.getSlatePlayersUseCase(c.tournamentId, "M").fold(
                onSuccess = { players = it },
                onFailure = { /* Not critical if this fails */ }
            )
        }

        isLoading = false
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(colors.background)
        }
    }) {
        // Header with live indicator
        ResponsiveHeader(
            title = "Entry Details",
            showBackButton = true,
            onBackClick = { NavigationState.navigate(Route.ContestLeaderboard(contestId)) },
            customActions = {
                // Live connection indicator
                if (realtimeState.isConnected && contest?.status == ContestStatus.LIVE) {
                    LiveIndicator()
                }
            }
        )

        // Contest Context Bar
        contest?.let { ContestContextBar(it, colors) }

        // Main Content
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            Div({
                style {
                    padding(24.px)
                    maxWidth(800.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
                when {
                    isLoading -> LoadingState(colors)
                    error != null -> ErrorState(error!!, colors)
                    entry != null && contest != null -> {
                        // Entry owner info
                        EntryOwnerCard(entry!!, isOwnEntry, colors)

                        // Stats card
                        EntryStatsCard(entry!!, contest!!, colors)

                        // Lineup display
                        if (lineup != null) {
                            LineupSection(lineup!!, players, colors)
                        } else {
                            NoLineupMessage(colors)
                        }

                        // Perfect lineup comparison for completed contests
                        if (contest!!.status == ContestStatus.COMPLETED && contest!!.perfectLineup != null && lineup != null) {
                            Div({ style { marginTop(24.px) } }) {
                                PerfectLineupSection(
                                    perfectLineup = contest!!.perfectLineup!!,
                                    userLineup = lineup,
                                    playersMap = players.associateBy { it.id },
                                    colors = colors
                                )
                            }
                        }

                        // Edit button for own entries when contest allows
                        if (isOwnEntry && contest!!.canEnter && entry != null) {
                            Div({
                                style {
                                    marginTop(24.px)
                                    display(DisplayStyle.Flex)
                                    justifyContent(JustifyContent.Center)
                                }
                            }) {
                                Button(
                                    text = "Edit Lineup",
                                    onClick = { NavigationState.navigate(Route.Lineup(contestId, entry!!.id)) },
                                    variant = "primary"
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun LoadingState(colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(48.px)
        }
    }) {
        Span({ style { color(colors.textSecondary) } }) {
            Text("Loading entry details...")
        }
    }
}

@Composable
private fun ErrorState(message: String, colors: ThemeColors) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        Span({ style { color(colors.error); fontSize(16.px) } }) {
            Text(message)
        }
    }
}

@Composable
private fun EntryOwnerCard(entry: EntryEntity, isOwnEntry: Boolean, colors: ThemeColors) {
    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(20.px)
            marginBottom(16.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(16.px)
            property("box-shadow", "0 2px 4px rgba(0,0,0,0.1)")
        }
    }) {
        // Avatar
        AvatarDisplay(
            avatarUrl = entry.avatarUrl,
            initials = entry.initials,
            size = AvatarSize.LARGE
        )

        // Name and badge
        Div({
            style {
                flex(1)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Span({
                    style {
                        fontSize(24.px)
                        fontWeight(700)
                        color(colors.textPrimary)
                    }
                }) {
                    Text(entry.formattedDisplayName)
                }

                if (isOwnEntry) {
                    Span({
                        style {
                            backgroundColor(colors.primary)
                            color(colors.textOnPrimary)
                            padding(4.px, 8.px)
                            borderRadius(12.px)
                            fontSize(12.px)
                            fontWeight(600)
                        }
                    }) {
                        Text("You")
                    }
                }
            }

            entry.username?.let { username ->
                Span({
                    style {
                        color(colors.textSecondary)
                        fontSize(14.px)
                    }
                }) {
                    Text("@$username")
                }
            }
        }
    }
}

@Composable
private fun EntryStatsCard(entry: EntryEntity, contest: ContestEntity, colors: ThemeColors) {
    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(20.px)
            marginBottom(24.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceAround)
            flexWrap(FlexWrap.Wrap)
            gap(16.px)
            property("box-shadow", "0 2px 4px rgba(0,0,0,0.1)")
        }
    }) {
        // Rank with context
        StatItem(
            label = "RANK",
            value = entry.rank?.let { "#$it of ${contest.currentEntries}" } ?: "-",
            colors = colors
        )

        // Percentile for larger contests
        if (entry.rank != null && contest.currentEntries >= 20) {
            val percentile = ((entry.rank!!.toDouble() / contest.currentEntries) * 100).toInt()
            StatItem(
                label = "PERCENTILE",
                value = "Top $percentile%",
                colors = colors,
                valueColor = if (percentile <= 10) colors.success else colors.textPrimary
            )
        }

        // Points
        StatItem(
            label = "POINTS",
            value = entry.totalPoints.asDynamic().toFixed(1) as String,
            colors = colors,
            valueColor = colors.primary
        )

        // Payout
        if (entry.payoutAmount > 0) {
            StatItem(
                label = "PAYOUT",
                value = "$${entry.payoutAmount.toInt()}",
                colors = colors,
                valueColor = colors.success
            )
        }
    }
}

@Composable
private fun StatItem(
    label: String,
    value: String,
    colors: ThemeColors,
    valueColor: CSSColorValue = colors.textPrimary
) {
    Div({
        style {
            textAlign("center")
        }
    }) {
        Div({
            style {
                color(colors.textSecondary)
                fontSize(11.px)
                fontWeight(600)
                letterSpacing(0.5.px)
                marginBottom(4.px)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                color(valueColor)
                fontSize(24.px)
                fontWeight(700)
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun LineupSection(
    lineup: LineupEntity,
    players: List<TennisPlayerEntity>,
    colors: ThemeColors
) {
    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(20.px)
            property("box-shadow", "0 2px 4px rgba(0,0,0,0.1)")
        }
    }) {
        H3({
            style {
                color(colors.textPrimary)
                fontSize(18.px)
                fontWeight(700)
                marginTop(0.px)
                marginBottom(16.px)
            }
        }) {
            Text("Lineup")
        }

        // Category slots
        val categories = listOf(
            Category.SERVE to lineup.slots.serve,
            Category.FOREHAND to lineup.slots.forehand,
            Category.BACKHAND to lineup.slots.backhand,
            Category.VOLLEYS to lineup.slots.volleys,
            Category.FIGHTING to lineup.slots.fighting
        )

        categories.forEach { (category, playerId) ->
            val player = playerId?.let { id -> players.find { it.id == id } }
            val categoryScore = lineup.categoryScores?.getScoreForCategory(category) ?: 0.0
            val breakdown = lineup.categoryBreakdowns?.getBreakdownForCategory(category)

            LineupSlotRow(
                category = category,
                player = player,
                playerId = playerId,
                points = categoryScore,
                breakdown = breakdown,
                colors = colors
            )
        }

        // Total row with salary progress bar
        Div({
            style {
                paddingTop(16.px)
                marginTop(16.px)
                borderTop(1.px, LineStyle.Solid, colors.border)
            }
        }) {
            // Salary budget with progress bar
            val salaryPercent = if (lineup.salaryCap > 0) {
                (lineup.totalSalaryCost.toDouble() / lineup.salaryCap * 100).toInt().coerceIn(0, 100)
            } else 0

            Div({
                style {
                    marginBottom(12.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        marginBottom(6.px)
                    }
                }) {
                    Span({
                        style {
                            fontSize(13.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text("Budget")
                    }
                    Span({
                        style {
                            fontSize(13.px)
                            color(colors.textPrimary)
                            fontWeight(500)
                        }
                    }) {
                        Text("\$${lineup.totalSalaryCost} / \$${lineup.salaryCap}")
                    }
                }
                // Progress bar
                Div({
                    style {
                        height(6.px)
                        backgroundColor(colors.border)
                        borderRadius(3.px)
                        overflow("hidden")
                    }
                }) {
                    Div({
                        style {
                            height(100.percent)
                            width(salaryPercent.percent)
                            backgroundColor(if (lineup.totalSalaryCost > lineup.salaryCap) colors.error else colors.primary)
                            borderRadius(3.px)
                        }
                    })
                }
            }

            // Total points row
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                Span({
                    style {
                        fontWeight(700)
                        color(colors.textPrimary)
                    }
                }) {
                    Text("Total Points")
                }
                Span({
                    style {
                        fontWeight(700)
                        fontSize(20.px)
                        color(colors.primary)
                    }
                }) {
                    Text("${lineup.totalPoints.asDynamic().toFixed(1)} pts")
                }
            }
        }
    }
}

@Composable
private fun LineupSlotRow(
    category: Category,
    player: TennisPlayerEntity?,
    playerId: String?,
    points: Double,
    breakdown: CategoryBreakdown?,
    colors: ThemeColors
) {
    // Get category-specific color
    val categoryColor = when (category) {
        Category.SERVE -> Colors.Category.Serve
        Category.FOREHAND -> Colors.Category.Forehand
        Category.BACKHAND -> Colors.Category.Backhand
        Category.VOLLEYS -> Colors.Category.Volleys
        Category.FIGHTING -> Colors.Category.Fighting
    }

    // Determine if row should be clickable (only when we have a valid player)
    val isClickable = player != null

    // Check if we have breakdown stats to show
    val hasBreakdown = breakdown != null && (breakdown.stats.isNotEmpty() || breakdown.quickFinishBonus > 0)

    Div({
        style {
            borderBottom(1.px, LineStyle.Solid, colors.border)
            marginLeft((-8).px)
            marginRight((-8).px)
        }
    }) {
        // Main row with player info
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                padding(12.px, 8.px)
                borderRadius(6.px)
                if (isClickable) {
                    cursor("pointer")
                    property("transition", "background-color 0.15s ease")
                }
            }
            if (isClickable && player != null) {
                onClick { NavigationState.navigate(Route.PlayerSkillDetail(player.id)) }
                onMouseOver { it.currentTarget.asDynamic().style.backgroundColor = colors.surfaceVariant.toString() }
                onMouseOut { it.currentTarget.asDynamic().style.backgroundColor = "transparent" }
            }
        }) {
            // Category badge with distinct color
            Span({
                style {
                    backgroundColor(categoryColor)
                    color(Colors.White)
                    padding(4.px, 8.px)
                    borderRadius(4.px)
                    fontSize(12.px)
                    fontWeight(600)
                    minWidth(80.px)
                    textAlign("center")
                }
            }) {
                Text(category.displayName)
            }

            // Player name section
            Div({
                style {
                    flex(1)
                    marginLeft(16.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                if (player != null) {
                    // Player name - styled as a link when clickable
                    Span({
                        style {
                            color(colors.primary)
                            fontWeight(500)
                        }
                    }) {
                        Text(player.name)
                    }
                    player.country.takeIf { it.isNotBlank() }?.let { country ->
                        Span({
                            style {
                                color(colors.textSecondary)
                                fontSize(12.px)
                            }
                        }) {
                            Text("($country)")
                        }
                    }
                    // View skills indicator
                    Span({
                        style {
                            color(colors.textSecondary)
                            fontSize(11.px)
                            opacity(0.7)
                        }
                    }) {
                        Text("\u2192") // Right arrow
                    }
                } else if (playerId != null) {
                    Span({
                        style {
                            color(colors.textSecondary)
                            fontStyle("italic")
                        }
                    }) {
                        Text("Player #${playerId.take(8)}")
                    }
                } else {
                    Span({
                        style {
                            color(colors.textSecondary)
                            fontStyle("italic")
                        }
                    }) {
                        Text("Empty slot")
                    }
                }
            }

            // Points
            Span({
                style {
                    color(if (points > 0) colors.primary else if (points < 0) colors.error else colors.textSecondary)
                    fontWeight(600)
                    minWidth(60.px)
                    textAlign("right")
                }
            }) {
                Text("${points.asDynamic().toFixed(1)} pts")
            }
        }

        // Breakdown row (always visible if stats exist)
        if (hasBreakdown && breakdown != null) {
            Div({
                style {
                    paddingLeft(104.px) // Align with player name (category badge width + margin)
                    paddingRight(8.px)
                    paddingBottom(8.px)
                    display(DisplayStyle.Flex)
                    flexWrap(FlexWrap.Wrap)
                    gap(8.px, 4.px)
                }
            }) {
                // Stat items
                breakdown.stats.forEach { item ->
                    val pointsText = if (item.points >= 0) "+${item.points.toInt()}" else "${item.points.toInt()}"
                    val pointsColor = if (item.points >= 0) colors.success else colors.error

                    Span({
                        style {
                            fontSize(11.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text("${item.count} ${item.stat} ")
                        Span({
                            style {
                                color(pointsColor)
                                fontWeight(500)
                            }
                        }) {
                            Text(pointsText)
                        }
                    }
                }

                // Quick Finish Bonus
                if (breakdown.quickFinishBonus > 0) {
                    Span({
                        style {
                            fontSize(11.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text("Quick Win ")
                        Span({
                            style {
                                color(colors.success)
                                fontWeight(500)
                            }
                        }) {
                            Text("+${breakdown.quickFinishBonus.toInt()}")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun NoLineupMessage(colors: ThemeColors) {
    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(32.px)
            textAlign("center")
            property("box-shadow", "0 2px 4px rgba(0,0,0,0.1)")
        }
    }) {
        Span({
            style {
                color(colors.textSecondary)
                fontSize(16.px)
            }
        }) {
            Text("No lineup submitted yet")
        }
    }
}

@Composable
private fun ContestContextBar(contest: ContestEntity, colors: ThemeColors) {
    Div({
        style {
            backgroundColor(colors.surface)
            padding(12.px, 24.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            flexWrap(FlexWrap.Wrap)
            gap(12.px)
            borderBottom(1.px, LineStyle.Solid, colors.border)
        }
    }) {
        // Left: Contest name + prize pool + type badge
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            Span({
                style {
                    fontWeight(600)
                    color(colors.textPrimary)
                    fontSize(14.px)
                }
            }) {
                Text("${contest.name} - \$${contest.prizePool.toInt()}")
            }
            ContestTypeBadge(contest.contestType)
        }

        // Right: Status badge
        ContestStatusBadge(contest.status)
    }
}

@Composable
private fun LiveIndicator() {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            backgroundColor(Color("rgba(76, 175, 80, 0.2)"))
            padding(4.px, 8.px)
            borderRadius(12.px)
        }
    }) {
        // Pulsing green dot
        Div({
            style {
                width(8.px)
                height(8.px)
                borderRadius(50.percent)
                backgroundColor(Colors.Success)
                property("animation", "pulse 2s infinite")
            }
        })
        Span({
            style {
                fontSize(11.px)
                color(Colors.White)
                fontWeight(500)
            }
        }) {
            Text("LIVE")
        }
    }
}
